import { ActionIcon, getDefaultZIndex, Modal, TextInput, type TextInputProps } from '@any-ui-react/core';
import classNames from 'classnames';
import { type CountryCode, getCountryCallingCode, parsePhoneNumberWithError } from 'libphonenumber-js';
import { type ChangeEvent, forwardRef, useEffect, useRef, useState } from 'react';

import 'country-flag-icons/3x2/flags.css';
import { HoverCardWithIcon } from '@/common/components/shared/hover-card';
import { MaterialIcons } from '@/common/components/shared/material-icons';
import { modalProps } from '@/common/components/shared/modal';
import { CountryPickerModalContent } from './CountryPickerModal';

const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });

export type PhoneDataChange = {
  countryCode?: CountryCode;
  number: string;
  isValid: boolean;
};

export type PhoneData = {
  countryCode?: CountryCode;
  isd?: string;
  number?: string | null;
};

export type PhoneNumberInputProps = Omit<TextInputProps, 'value' | 'onChange' | 'defaultValue'> & {
  defaultValue?: PhoneData | null;
  value?: PhoneData | null;
  onChange?: (data: PhoneDataChange) => void;
  // A country to sort near the top of the Country Code picker (after the currently-set one) when
  // known - e.g. the edited employee's current main contract company's country, or the company
  // being chosen in Add mode.
  preferredCountryCode?: CountryCode;
};

const toDisplayValue = (phoneData?: PhoneData | null): string => {
  if (!phoneData?.number) return '';
  try {
    const parsed = phoneData.countryCode
      ? parsePhoneNumberWithError(phoneData.number, phoneData.countryCode as CountryCode)
      : parsePhoneNumberWithError(phoneData.number);
    return parsed.formatNational();
  } catch {
    return phoneData.number;
  }
};

const parseInput = (input: string, hint?: CountryCode): PhoneDataChange => {
  if (!input) return { number: '', isValid: false };
  try {
    const parsed = parsePhoneNumberWithError(input, hint);
    return {
      countryCode: parsed.country ?? hint,
      number: parsed.nationalNumber as string,
      isValid: parsed.isValid(),
    };
  } catch {
    return { number: input, isValid: false };
  }
};

export const PhoneNumberInput = forwardRef<HTMLInputElement, PhoneNumberInputProps>(
  ({ disabled, error, value, defaultValue, onChange, preferredCountryCode, ...rest }, ref) => {
    const [pickerOpen, setPickerOpen] = useState(false);

    const [displayValue, setDisplayValue] = useState(() => toDisplayValue(value || defaultValue));
    const [countryCode, setCountryCode] = useState<CountryCode | undefined>(
      () => (value || defaultValue)?.countryCode as CountryCode | undefined,
    );
    const [isValid, setIsValid] = useState(() => {
      const initial = value || defaultValue;
      if (!initial?.number) return true;
      return parseInput(toDisplayValue(initial), initial.countryCode as CountryCode).isValid;
    });

    // Prevents the sync useEffect from overwriting the user's in-progress input.
    // Set to true on user-initiated changes; the effect clears it after skipping once.
    const isOwnChange = useRef(false);

    const syncCountryCode = (value || defaultValue)?.countryCode;
    const syncNumber = (value || defaultValue)?.number;
    useEffect(() => {
      if (isOwnChange.current) {
        isOwnChange.current = false;
        return;
      }
      const synced = { countryCode: syncCountryCode as CountryCode, number: syncNumber };
      setDisplayValue(toDisplayValue(synced));
      setCountryCode(syncCountryCode as CountryCode | undefined);
      setIsValid(!syncNumber || parseInput(toDisplayValue(synced), syncCountryCode as CountryCode).isValid);
    }, [syncCountryCode, syncNumber]);

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      const input = e.target.value;
      isOwnChange.current = true;
      setDisplayValue(input);
      const result = parseInput(input, countryCode);
      setIsValid(!input || result.isValid);
      onChange?.({ ...result, countryCode });
    };

    const handleBlur = () => {
      if (!countryCode) return;
      try {
        const parsed = parsePhoneNumberWithError(displayValue, countryCode);
        if (parsed.isValid()) {
          setDisplayValue(parsed.formatNational());
        }
      } catch {
        // keep displayValue as-is for invalid/incomplete input
      }
    };

    const handleCountrySelect = (selectedCode: CountryCode) => {
      isOwnChange.current = true;
      const result = parseInput(displayValue, selectedCode);
      setCountryCode(selectedCode);
      setIsValid(!displayValue || result.isValid);
      onChange?.({ ...result, countryCode: selectedCode });
      setPickerOpen(false);
    };

    const internalError = !isValid && !!displayValue && 'Enter a valid phone number';

    return (
      <>
        <Modal
          centered={true}
          closeOnClickOutside={false}
          closeOnEscape={false}
          opened={pickerOpen}
          scrollAreaComponent={props => <div {...props} className="flex flex-col flex-nowrap" />}
          size="md"
          title="Select Country Code"
          withCloseButton={false}
          zIndex={getDefaultZIndex('modal') + 2}
          classNames={{
            ...modalProps.classNames,
            body: `${modalProps.classNames?.body ?? ''} flex-1`,
          }}
          onClose={() => setPickerOpen(false)}
        >
          {pickerOpen && (
            <CountryPickerModalContent
              initialCountryCode={countryCode}
              preferredCountryCode={preferredCountryCode}
              onApply={handleCountrySelect}
              onCancel={() => setPickerOpen(false)}
            />
          )}
        </Modal>
        <TextInput
          {...rest}
          ref={ref}
          className={classNames(rest.className, 'tabular-nums')}
          disabled={disabled}
          error={error ?? internalError}
          leftSection={
            <HoverCardWithIcon
              customTarget={
                <ActionIcon disabled={disabled} tabIndex={-1} onClick={() => setPickerOpen(true)}>
                  {countryCode ? (
                    <span className={classNames(`flag:${countryCode}`, 'shrink-0 border border-gray-4')} />
                  ) : (
                    <MaterialIcons
                      fontSize="24px"
                      iconName="language_international"
                      className="cursor-pointer text-blue-6"
                    />
                  )}
                </ActionIcon>
              }
              position="bottom-start"
            >
              {countryCode ? (
                <>
                  <div>
                    +<span className="tabular-nums">{getCountryCallingCode(countryCode)}</span> -&nbsp;
                    {regionNames.of(countryCode)}
                  </div>
                  <div>Click here to change country code</div>
                </>
              ) : (
                'Select country code'
              )}
            </HoverCardWithIcon>
          }
          leftSectionWidth={32}
          placeholder={!countryCode ? '← Select country code first' : '03-6384-5540'}
          value={displayValue}
          onBlur={handleBlur}
          onChange={handleChange}
        />
      </>
    );
  },
);
